import React, { Fragment, useMemo, useEffect, useState } from 'react';
import { useTable, useSortBy, usePagination, useFilters } from 'react-table';
import Select from "react-select";

const OptionsDisplayChart = ({ options }) => {

  const [selectedFilters,setSelectedFilters] = useState([]);

  const selectedFilterHeaders = useMemo(() => {
    return new Set(selectedFilters.map(filter => filter.value));
  }, [selectedFilters]);

  const containsTextFilter = (rows, id, filterValue) => {
    return rows.filter(row => (filterValue.length === 0 || filterValue.includes(row.values[id])));
  };

  const filterTypes = useMemo(() => ({
    containsText: containsTextFilter
  }), []);

  const SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id } }) => {
    const options = useMemo(() => {
      return [...new Set(preFilteredRows.filter(row => row.values[id] !== '')
        .map(row => row.values[id])
        .sort())]
        .map(option => ({
          label: option,
          value: option
        }));
    }, [id, preFilteredRows]);

    useEffect(() => {
      if (filterValue && filterValue.length > 0 && !options.some(option => filterValue.includes(option.value))) {
        setFilter([]);
      }
    }, [filterValue, options, setFilter]);

    return (
      <Select
        isMulti
        options={options}
        value={filterValue ? filterValue.map(val => ({label: val, value: val})) : []}
        onChange={selectedOptions => setFilter(selectedOptions.map(option => option.value))}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 3
          })
        }}
      />
    )
  };
  
  const columns = useMemo(() => {
    return (!Array.isArray(options) || options.length === 0) ? [] : 
        Object.keys(options[0]).map(key => ({
            Header: key,
            accessor: key,
            Filter: SelectColumnFilter,
            filter: 'containsText'
    }));
  }, [options]);

  const selectedFilterOptions = useMemo(() => {
    return columns.map(col => ({
      label: col.Header,
      value: col.Header
    }));
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageOptions,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: (!Array.isArray(options) || options.length === 0) ? [] : options,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 50 }
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const handleSelectChange = (selectedOptions) => {
    const selectedHeaders = new Set(selectedOptions.map(option => option.value));

    setSelectedFilters(selectedOptions);

    headerGroups[0].headers.forEach(column => {
      if (column.canFilter && !selectedHeaders.has(column.Header)) {
        column.setFilter([]);
      }
    });
  }

  return (
    <div>
      <div className='row'>
        <div className='col s12'>
          <Select
            isMulti
            options={selectedFilterOptions}
            value={selectedFilters}
            placeholder="Select Filters..."
            onChange={handleSelectChange}
            menuPosition="fixed"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 3
              })
            }}
          />
        </div>
      </div>
      <div className='row'>
        {headerGroups[0].headers.map(column => (
          (column.canFilter && selectedFilterHeaders.has(column.Header)) && (
            <div className='col s12 m6 l3' key={column.id}>
              <label className='black-text' style={{ fontWeight: 'bold' }}>{column.render('Header')}</label>
              {column.render('Filter')}
            </div>
          )
        ))}
      </div>
      <table {...getTableProps()} className="highlight centered responsive-table">
        <thead>
          {headerGroups.map(headerGroup => (
            <Fragment key={headerGroup.id}>
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                    <th
                      key={column.id}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={{
                        cursor: 'pointer',
                        position: 'sticky',
                        top: 60,
                        background: 'white',
                        zIndex: 2
                      }}>
                      {column.render('Header')}
                      {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                    </th>
                ))}
              </tr>
            </Fragment>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr key={i} {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td key={cell.column.id} {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="center-align" style={{ marginTop: '1rem' }}>
        <button className="btn waves-effect waves-light blue" onClick={() => previousPage()} disabled={!canPreviousPage}>
          Previous
        </button>
        <span style={{ margin: '0 1rem' }}>
          Page {pageIndex + 1} of {pageOptions.length}
        </span>
        <button className="btn waves-effect waves-light blue" onClick={() => nextPage()} disabled={!canNextPage}>
          Next
        </button>
      </div>
    </div>
  );
};

export default OptionsDisplayChart;